import { useMemo, useState } from 'react';
import { Card, CardHeader } from '../../../../components/ui/Card';
import { useCitationRecommendations, useUpdateRecommendationStatus } from '../../../../hooks/useMeasurement';
import { LoadingState } from '../../../../components/ui/States';

type Status = 'open' | 'done' | 'dismissed';
type Tab = 'all' | 'earned' | 'owned' | 'impact';

const RECOMMENDATION_KIND_LABELS: Record<string, string> = {
  win_back_query: 'Win back a query',
  fix_url: 'Fix broken URL',
  improve_content: 'Improve content',
  create_content: 'Create content',
};

const OWNED_KINDS = new Set(['improve_content', 'create_content']);
const IMPACT_KINDS = new Set(['fix_url']);

function bucketFor(kind: string): 'earned' | 'owned' | 'impact' {
  if (OWNED_KINDS.has(kind)) return 'owned';
  if (IMPACT_KINDS.has(kind)) return 'impact';
  return 'earned';
}

export function ActionsPage() {
  const [status, setStatus] = useState<Status>('open');
  const [tab, setTab] = useState<Tab>('all');
  const { data, isLoading } = useCitationRecommendations(status);
  const update = useUpdateRecommendationStatus();

  const recs = data?.recommendations ?? [];

  const counts = useMemo(() => {
    const c = { earned: 0, owned: 0, impact: 0 };
    for (const r of recs) c[bucketFor(r.kind)]++;
    return c;
  }, [recs]);

  const filtered = useMemo(() => {
    if (tab === 'all') return recs;
    return recs.filter((r) => bucketFor(r.kind) === tab);
  }, [recs, tab]);

  return (
    <div className="space-y-4">
      <Card padding="md">
        <CardHeader
          title="Recommendations"
          description="Concrete next actions, generated from the latest sweep. Earned (visibility wins), Owned (your content), Impact (broken links and tech issues)."
          action={
            <div className="inline-flex rounded-lg border border-border bg-surface-primary p-0.5 text-xs">
              {(['open', 'done', 'dismissed'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`rounded-md px-3 py-1.5 capitalize transition-colors ${
                    status === s
                      ? 'border border-border font-semibold text-text-primary'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          }
        />

        <nav className="flex gap-1 border-b border-border-light mb-4 -mt-2">
          <TabButton active={tab === 'all'} onClick={() => setTab('all')} label={`All · ${recs.length}`} />
          <TabButton active={tab === 'earned'} onClick={() => setTab('earned')} label={`Earned · ${counts.earned}`} hint="Win back queries where competitors are cited but we aren't" />
          <TabButton active={tab === 'owned'} onClick={() => setTab('owned')} label={`Owned · ${counts.owned}`} hint="Improvements to pages you control" />
          <TabButton active={tab === 'impact'} onClick={() => setTab('impact')} label={`Impact · ${counts.impact}`} hint="Broken links and tech blockers" />
        </nav>

        {isLoading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <p className="py-10 text-center text-body-sm text-text-tertiary">
            {status === 'open'
              ? 'No open recommendations in this bucket — run a sweep or check the other tabs.'
              : `No ${status} recommendations.`}
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.sort((a, b) => a.priority - b.priority).map((rec) => (
              <div
                key={rec.id}
                className="border border-border-light rounded-lg p-3 flex items-start gap-3"
              >
                <PriorityBadge priority={rec.priority} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-body-xs uppercase tracking-wide text-text-tertiary">
                      {RECOMMENDATION_KIND_LABELS[rec.kind] ?? rec.kind}
                    </span>
                  </div>
                  <p className="text-body-sm font-medium text-text-primary">{rec.title}</p>
                  <p className="mt-1 text-body-xs text-text-secondary leading-relaxed">{rec.rationale}</p>
                </div>
                {status === 'open' ? (
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => update.mutate({ id: rec.id, status: 'done' })}
                      className="text-body-xs text-text-secondary hover:text-success px-2 py-1 rounded border border-border-light"
                    >
                      Done
                    </button>
                    <button
                      onClick={() => update.mutate({ id: rec.id, status: 'dismissed' })}
                      className="text-body-xs text-text-tertiary hover:text-error px-2 py-1 rounded border border-border-light"
                    >
                      Dismiss
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => update.mutate({ id: rec.id, status: 'open' })}
                    className="text-body-xs text-text-secondary hover:text-text-primary px-2 py-1 rounded border border-border-light flex-shrink-0"
                  >
                    Reopen
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function TabButton({ active, onClick, label, hint }: { active: boolean; onClick: () => void; label: string; hint?: string }) {
  return (
    <button
      onClick={onClick}
      title={hint}
      className={`px-3 py-2 text-body-sm font-medium border-b-2 -mb-px ${
        active ? 'border-text-primary text-text-primary' : 'border-transparent text-text-tertiary hover:text-text-primary'
      }`}
    >
      {label}
    </button>
  );
}

function PriorityBadge({ priority }: { priority: number }) {
  const label = priority === 1 ? 'P1' : priority === 2 ? 'P2' : 'P3';
  const color =
    priority === 1
      ? 'bg-error-light text-error-dark'
      : priority === 2
        ? 'bg-warning-light text-warning-dark'
        : 'bg-surface-tertiary text-text-secondary';
  return (
    <span className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-semibold flex-shrink-0 ${color}`}>
      {label}
    </span>
  );
}

export function useActionsCount(): number {
  const { data } = useCitationRecommendations('open');
  return data?.recommendations.length ?? 0;
}
